/**
 * The details half of an EXPANDED card — beside the summary, or under it on a narrow board: everything behind the card's headline, as a searchable
 * list or as a row of picture tiles.
 *
 * One component for every card, because the question is the same on all of them — "which ones
 * were they" — and an operator who has learnt to search the vehicle list should not meet a
 * differently shaped presence list. Each card maps its records into `DetailItem`s; this file
 * decides nothing about what a sighting, an employee or a camera is.
 *
 * ── Fits the tile, scrolls inside ──
 *
 * The panel takes whatever height its card has left (`flex-1`), and only the list body scrolls.
 * A card that grew to fit its list would push every card below it down the page and turn the
 * grid into a column.
 *
 * ── Search is always on ──
 *
 * Layout choices (List vs Cards, Shrink vs Expand) only change in Customize mode; search does
 * not, because it is a question asked of the data, not an arrangement of the board.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ClockIcon, MagnifyingGlassIcon, PhotoIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAlertImage } from '../../alerts/useAlertImage';
import { useBoardCard, SUNK, tint, FOCUS } from './BoardParts';
import { useElementSize } from './useElementSize';

export interface DetailBadge {
  text: string;
  color?: string;
  /** People are circles; things (plates, cameras, counts) are rounded squares. */
  shape?: 'circle' | 'square';
}

export interface DetailItem {
  id: string;
  title: string;
  sub?: string;
  badge?: DetailBadge;
  /** A directly loadable picture for the row thumbnail and the tile. */
  image?: string | null;
  /**
   * Lazy resolver, for pictures that are not a plain URL on the row: the cloud tunnel's blobs,
   * an employee's enrollment photo, a camera's latest event frame. Called once per row, when
   * the row first scrolls into view.
   */
  loadImage?: () => Promise<string | null>;
  /**
   * Bumped when the picture becomes available after the row was drawn (an alert's
   * `previewReady`) — re-runs `loadImage`, which may have been too early to succeed.
   */
  previewReady?: number;
  /** Detection box on the picture, as fractions of the frame (0..1). */
  box?: { x: number; y: number; w: number; h: number } | null;
  /** Second and third columns of the list. Strings, so search can read them. */
  c2?: string;
  c3?: string;
  status?: { label: string; color: string };
  /** The right-hand column — a clock reading, or a short figure. */
  time?: string;
  /** Tile footer tag: a type, a match %, in/out. */
  tag?: string;
  /** Tile caption over the picture — where it was seen. */
  place?: string;
  /** Anything else a search should match. */
  keywords?: string;
  /** The summary filters this item answers to ("present", "known", a watch-list category). */
  groups?: string[];
  /** Whatever the card needs to open this item's record — handed back by `onOpenItem`. */
  data?: unknown;
  onOpen?: () => void;
}

export interface DetailsPanelProps {
  /** "ALL DETECTED VEHICLES". */
  title: string;
  /** The count pill — "24 detected · 0 watch-listed". */
  count?: string;
  placeholder?: string;
  /** Header labels for the five list columns: main, 2, 3, status, right. */
  columns: [string, string, string, string, string];
  items: DetailItem[];
  /**
   * Every row shows a picture frame, a placeholder where there is none (alerts). Without it a
   * row shows its picture when it has one and its badge when it has not.
   */
  thumbs?: boolean;
  loading?: boolean;
  error?: string | null;
  /** Said when there is nothing at all, before any search. */
  empty?: string;
  /** More exists beyond `items` — a button at the end of the list pulls it. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Overrides the card's own List/Cards choice (the alert card draws its own timeline). */
  view?: 'list' | 'cards';
  /** Extra controls in the bar, before the search box. */
  actions?: ReactNode;
  /**
   * Open an item's record, given the items on screen (filtered and searched) in order — so a
   * record viewer can page through exactly the rows the operator was looking at. Used for any
   * item without its own `onOpen`.
   */
  onOpenItem?: (item: DetailItem, shown: DetailItem[]) => void;
}

const ROW_COLS = 'minmax(0,2.4fr) minmax(0,1.1fr) minmax(0,1.1fr) 116px 88px';
const ROW_COLS_NARROW = 'minmax(0,1fr) 104px 76px';

function matches(it: DetailItem, q: string) {
  const hay = [it.title, it.sub, it.c2, it.c3, it.status?.label, it.time, it.tag, it.place, it.keywords]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export function StatusPill({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="inline-flex max-w-full items-center truncate whitespace-nowrap rounded-full px-[9px] py-[2px] text-[11px] font-semibold"
      style={{ color, backgroundColor: tint(color, 14) }}
    >
      {label}
    </span>
  );
}

export function Badge({ badge, size = 30 }: { badge: DetailBadge; size?: number }) {
  const color = badge.color || 'var(--muted-foreground)';
  return (
    <span
      className={`flex flex-none items-center justify-center font-bold uppercase text-white ${badge.shape === 'square' ? 'rounded-md' : 'rounded-full'}`}
      style={{ width: size, height: size, backgroundColor: color, fontSize: size <= 24 ? 9 : 10 }}
      aria-hidden
    >
      {badge.text}
    </span>
  );
}

/** A picture placeholder that still looks like a frame — the design's gradient stand-in. */
function FramePlaceholder({ seed }: { seed: string }) {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) % 360;
  return (
    <div
      className="absolute inset-0"
      style={{ background: `linear-gradient(160deg, hsl(${h} 18% 46%), hsl(${(h + 40) % 360} 14% 22%))` }}
      aria-hidden
    />
  );
}

/**
 * The row's picture. The loader handed to `useAlertImage` must keep its identity across
 * renders — the hook re-resolves whenever it changes, and the items are rebuilt on every poll,
 * so an inline arrow would refetch (and flicker) every row's picture forever.
 */
function useItemImage(item: DetailItem) {
  const load = useRef(item.loadImage);
  load.current = item.loadImage;
  const loader = useCallback(() => (load.current ? load.current() : Promise.resolve(null)), []);
  return useAlertImage(item, (i) => i.image || null, item.loadImage ? loader : undefined);
}

/** A row's picture: a frame for things, a circle for people (whose badge is a circle). */
function RowThumb({ item, placeholder }: { item: DetailItem; placeholder: boolean }) {
  const img = useItemImage(item);
  const ring = item.status?.color || 'var(--border)';
  if (item.badge?.shape === 'circle') {
    return (
      <span ref={img.ref} className="relative flex h-[34px] w-[34px] flex-none items-center justify-center">
        {img.src ? (
          <img
            src={img.src}
            alt=""
            loading="lazy"
            onError={img.markFailed}
            className="h-full w-full rounded-full object-cover"
            style={{ boxShadow: `0 0 0 1.5px ${ring}` }}
          />
        ) : (
          <Badge badge={item.badge} size={34} />
        )}
      </span>
    );
  }
  if (!img.src && !placeholder && item.badge) {
    return (
      <span ref={img.ref} className="flex h-[34px] w-[60px] flex-none items-center">
        <Badge badge={item.badge} />
      </span>
    );
  }
  return (
    <span
      ref={img.ref}
      className="relative h-[34px] w-[60px] flex-none overflow-hidden rounded-md"
      style={{ boxShadow: `0 0 0 1.5px ${ring}` }}
    >
      {img.src ? (
        <img src={img.src} alt="" loading="lazy" onError={img.markFailed} className="h-full w-full object-cover" />
      ) : (
        <FramePlaceholder seed={item.id} />
      )}
    </span>
  );
}

function ListRow({ item, narrow, thumbs }: { item: DetailItem; narrow: boolean; thumbs: boolean }) {
  const Tag = item.onOpen ? 'button' : 'div';
  return (
    <Tag
      {...(item.onOpen ? { type: 'button' as const, onClick: item.onOpen } : {})}
      className={`grid h-[50px] w-full flex-none items-center gap-3 border-b border-border px-4 text-left transition-colors ${item.onOpen ? `hover:bg-black/[0.03] dark:hover:bg-white/[0.04] ${FOCUS}` : ''}`}
      style={{ gridTemplateColumns: narrow ? ROW_COLS_NARROW : ROW_COLS }}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        {thumbs || item.image || item.loadImage ? (
          <RowThumb item={item} placeholder={thumbs} />
        ) : item.badge ? (
          <Badge badge={item.badge} />
        ) : null}
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold leading-tight text-foreground" title={item.title}>
            {item.title}
          </span>
          {item.sub && (
            <span className="block truncate text-[11px] leading-tight text-muted-foreground" title={item.sub}>
              {item.sub}
            </span>
          )}
        </span>
      </span>
      {!narrow && <span className="truncate text-[13px] text-foreground" title={item.c2}>{item.c2 || '—'}</span>}
      {!narrow && <span className="truncate text-[13px] text-muted-foreground" title={item.c3}>{item.c3 || '—'}</span>}
      <span className="min-w-0">{item.status ? <StatusPill {...item.status} /> : null}</span>
      <span className="truncate text-right text-[12px] tabular-nums text-muted-foreground">{item.time || ''}</span>
    </Tag>
  );
}

/** `grid`: a cell of a wrapping grid (vertical card) rather than a fixed-width rail stop. */
function Tile({ item, grid = false }: { item: DetailItem; grid?: boolean }) {
  const img = useItemImage(item);
  const color = item.status?.color || 'var(--border)';
  const Tag = item.onOpen ? 'button' : 'div';
  return (
    <Tag
      {...(item.onOpen ? { type: 'button' as const, onClick: item.onOpen } : {})}
      className={`group flex ${grid ? 'w-full' : 'w-[216px] flex-none snap-start'} flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-card transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(16,24,40,0.12)] ${FOCUS}`}
      style={{ borderTop: `3px solid ${color}` }}
    >
      <span ref={img.ref} className={`relative block overflow-hidden bg-muted ${grid ? 'aspect-video flex-none' : 'min-h-[112px] flex-1'}`}>
        {img.src ? (
          <img src={img.src} alt="" loading="lazy" onError={img.markFailed} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <FramePlaceholder seed={item.id} />
        )}
        {img.src && item.box && (
          <span
            className="absolute rounded-[2px]"
            style={{
              left: `${item.box.x * 100}%`,
              top: `${item.box.y * 100}%`,
              width: `${item.box.w * 100}%`,
              height: `${item.box.h * 100}%`,
              border: `1.5px solid ${color}`,
            }}
            aria-hidden
          />
        )}
        <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" aria-hidden />
        {item.badge && (
          <span className="absolute left-2 top-2 rounded-full" style={{ boxShadow: '0 0 0 2px #fff', borderRadius: item.badge.shape === 'square' ? 6 : 9999 }}>
            <Badge badge={item.badge} size={24} />
          </span>
        )}
        {item.status && (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-white px-2 py-[2px] text-[9px] font-bold uppercase tracking-wide text-gray-900">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.status.color }} aria-hidden />
            {item.status.label}
          </span>
        )}
        {!img.src && !item.badge && (
          <PhotoIcon className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-white/50" aria-hidden />
        )}
        {item.place && (
          <span className="absolute bottom-2 left-2 right-2 flex items-center gap-1 truncate text-[10px] font-semibold uppercase tracking-wide text-white">
            <VideoCameraIcon className="h-3 w-3 flex-none" aria-hidden />
            <span className="truncate">{item.place}</span>
          </span>
        )}
      </span>
      <span className="block flex-none px-3 pb-2.5 pt-2">
        <span className="block truncate text-[14px] font-semibold leading-tight text-foreground" title={item.title}>{item.title}</span>
        <span className="block truncate text-[11px] leading-tight text-muted-foreground">{item.sub || ' '}</span>
        <span className="mt-2 flex items-center gap-1.5 border-t border-border pt-2">
          <ClockIcon className="h-3 w-3 flex-none text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-[12px] font-bold tabular-nums text-foreground">{item.time || '—'}</span>
          {item.tag && (
            <span className="flex-none truncate rounded-md px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" style={{ backgroundColor: SUNK }}>
              {item.tag}
            </span>
          )}
        </span>
      </span>
    </Tag>
  );
}

export default function DetailsPanel({
  title,
  count,
  placeholder = 'Search…',
  columns,
  items,
  thumbs = false,
  loading = false,
  error,
  empty = 'Nothing to show yet.',
  onLoadMore,
  loadingMore,
  view: viewOverride,
  actions,
  onOpenItem,
}: DetailsPanelProps) {
  const card = useBoardCard();
  const view = viewOverride || card.view;
  const [q, setQ] = useState('');
  const [ref, { width }] = useElementSize<HTMLDivElement>();
  const narrow = width > 0 && width <= 620;

  const needle = q.trim().toLowerCase();
  const filterKey = card.filter?.key ?? null;
  const shown = useMemo(() => {
    const kept = items.filter((it) => (!filterKey || !!it.groups?.includes(filterKey)) && (!needle || matches(it, needle)));
    if (!onOpenItem) return kept;
    return kept.map((it) => (it.onOpen ? it : { ...it, onOpen: () => onOpenItem(it, kept) }));
  }, [items, needle, filterKey, onOpenItem]);
  const narrowed = !!needle || !!filterKey;

  let body: ReactNode;
  if (loading && items.length === 0) {
    body = (
      <div className="space-y-2 p-4" aria-hidden>
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  } else if (error && items.length === 0) {
    body = <p className="px-4 py-6 text-center text-[12px] text-destructive">{error}</p>;
  } else if (items.length === 0) {
    body = <p className="px-4 py-6 text-center text-[12px] text-muted-foreground">{empty}</p>;
  } else if (shown.length === 0) {
    body = (
      <p className="px-4 py-6 text-center text-[12px] text-muted-foreground">
        {needle ? `Nothing matches “${q.trim()}”${card.filter ? ` in ${card.filter.label}` : ''}.` : `Nothing in ${card.filter?.label}.`}
      </p>
    );
  } else if (view === 'cards' && card.size === 'expand' && !card.split) {
    // Under the summary (a vertical card): tiles wrap into a grid that scrolls DOWN, like the
    // list does — a sideways rail in a card this narrow would show one tile and a half.
    body = (
      <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 overflow-y-auto px-4 pb-3.5 pt-3">
        {shown.map((it) => <Tile key={it.id} item={it} grid />)}
        {onLoadMore && !needle && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className={`col-span-full rounded-xl border border-dashed border-border py-2.5 text-[12px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60 ${FOCUS}`}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    );
  } else if (view === 'cards') {
    body = (
      <div className="flex h-full min-h-0 snap-x gap-3 overflow-x-auto overflow-y-hidden px-4 pb-3.5 pt-3">
        {shown.map((it) => <Tile key={it.id} item={it} />)}
        {onLoadMore && !needle && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className={`flex w-[120px] flex-none items-center justify-center rounded-xl border border-dashed border-border text-[12px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60 ${FOCUS}`}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    );
  } else {
    body = (
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className="grid flex-none gap-3 border-b border-t border-border px-4 py-[7px] text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
          style={{ gridTemplateColumns: narrow ? ROW_COLS_NARROW : ROW_COLS }}
        >
          <span className="truncate">{columns[0]}</span>
          {!narrow && <span className="truncate">{columns[1]}</span>}
          {!narrow && <span className="truncate">{columns[2]}</span>}
          <span className="truncate">{columns[3]}</span>
          <span className="truncate text-right">{columns[4]}</span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {shown.map((it) => <ListRow key={it.id} item={it} narrow={narrow} thumbs={thumbs} />)}
          {onLoadMore && !needle && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className={`flex-none py-2.5 text-[12px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60 ${FOCUS}`}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`flex min-h-0 flex-1 flex-col ${card.split ? '' : 'border-t border-border'}`}>
      <div className="flex flex-none flex-wrap items-center gap-2 px-4 py-[9px]" style={{ backgroundColor: SUNK }}>
        <h4 className="truncate text-[12px] font-bold uppercase tracking-wide text-foreground">{title}</h4>
        {count && (
          <span className="flex-none rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            {narrowed ? `${shown.length} of ${items.length}` : count}
          </span>
        )}
        {card.filter && (
          <button
            type="button"
            onClick={() => card.setFilter(null)}
            title="Show everything"
            className={`flex max-w-[180px] flex-none items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold text-white ${FOCUS}`}
          >
            <span className="truncate">{card.filter.label}</span>
            <XMarkIcon className="h-3 w-3 flex-none" aria-hidden />
          </button>
        )}
        <span className="flex-1" />
        {actions}
        <label className="relative flex-none">
          <span className="sr-only">{placeholder}</span>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className={`h-[30px] w-[220px] max-w-[46vw] rounded-lg border border-border bg-card pl-8 pr-2 text-[12px] text-foreground placeholder:text-muted-foreground ${FOCUS}`}
          />
        </label>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{body}</div>
    </div>
  );
}

